import React, { useCallback, useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import useShippingRequest from '../../hooks/useShippingRequest';
import { useLocale } from '../../i18n/LanguageContext';
import { usd, validCents } from '../distributor-cash/cashMoney';
import MailboxReviewForm from './MailboxReviewForm';

const unwrap = (response) => response?.data?.data;
const idPattern = /^[a-f\d]{24}$/i;
const emptyReview = {
  id: '',
  alertId: '',
  userId: '',
  expectedRevision: 0,
  outcome: 'sent',
  messageId: '',
  reason: '',
  confirmedMailbox: false,
};
const statusLabels = {
  pending: 'Queued',
  sending: 'Sending — outcome unconfirmed',
  needs_review: 'Needs mailbox review',
};
const roleLabels = {
  admin: 'MedEffects admin',
  master_distributor: 'Distributor user',
  unavailable: 'Account unavailable',
};
const rowKey = (row) => `${row.alertId}:${row.userId}`;

export default function BankAlertDeliveryQueue() {
  const locale = useLocale();
  const [queue, setQueue] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [review, setReview] = useState(emptyReview);
  const { begin, version } = useShippingRequest('bank-alert-deliveries');
  const load = useCallback(
    async (nextPage) => {
      if (busyRef.current) return;
      const request = begin();
      setLoading(true);
      setQueue(null);
      setReview(emptyReview);
      try {
        const data = unwrap(
          await distributorService.adminBankAlertDeliveries(nextPage, { signal: request.signal })
        );
        if (
          !Array.isArray(data?.data) ||
          data.data.length > 20 ||
          !Number.isSafeInteger(data.total) ||
          data.total < 0 ||
          data.totalPages !== Math.ceil(data.total / 20) ||
          !Number.isSafeInteger(data.page) ||
          data.page < 1 ||
          data.page > Math.max(1, data.totalPages) ||
          typeof data.emailEnabled !== 'boolean' ||
          new Set(data.data.map(rowKey)).size !== data.data.length ||
          data.data.some(
            (row) =>
              !idPattern.test(row?.alertId) ||
              !idPattern.test(row.userId) ||
              !idPattern.test(row.distributorId) ||
              typeof row.recipientName !== 'string' ||
              typeof row.distributorName !== 'string' ||
              !Object.hasOwn(roleLabels, row.recipientRole) ||
              (row.currency === 'USD'
                ? !validCents(row.remainingCents) || !validCents(row.thresholdCents)
                : row.currency !== undefined ||
                  typeof row.familyKey !== 'string' ||
                  !Number.isFinite(row.remainingUnits)) ||
              !Number.isSafeInteger(row.revision) ||
              row.revision < 0 ||
              !Object.hasOwn(statusLabels, row.status) ||
              typeof row.canReview !== 'boolean' ||
              (row.canReview && !['sending', 'needs_review'].includes(row.status))
          )
        )
          throw new Error('Invalid alert email queue');
        if (!request.isCurrent()) return;
        setQueue(data);
        setPage(data.page);
        setUncertain(false);
        setError((current) =>
          current
            ? 'Queue refreshed. Review this attempt in the sending mailbox before another decision.'
            : ''
        );
      } catch {
        if (request.isCurrent()) setError('Low-bank email queue is unavailable. Please retry.');
      } finally {
        if (request.isCurrent()) setLoading(false);
      }
    },
    [begin]
  );
  useEffect(() => {
    busyRef.current = false;
    setBusy(false);
    setError('');
    setNotice('');
    setUncertain(false);
    setReview(emptyReview);
    load(1);
  }, [load, version]);
  const reconcile = async (event) => {
    event.preventDefault();
    const captured = { ...review };
    const row = queue?.data.find((item) => rowKey(item) === captured.id);
    if (
      busyRef.current ||
      loading ||
      uncertain ||
      !row?.canReview ||
      row.revision !== captured.expectedRevision ||
      !captured.confirmedMailbox ||
      captured.reason.trim().length < 12 ||
      captured.reason.length > 300 ||
      !['sent', 'not_sent'].includes(captured.outcome) ||
      (captured.outcome === 'sent' &&
        !/^(?:[A-Za-z0-9._:@-]{6,200}|<[A-Za-z0-9._:@-]{4,198}>)$/.test(captured.messageId))
    )
      return;
    busyRef.current = true;
    const request = begin();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const receipt = unwrap(
        await distributorService.reconcileBankAlertDelivery(captured.alertId, captured.userId, {
          expectedRevision: captured.expectedRevision,
          outcome: captured.outcome,
          confirmedMailbox: true,
          reason: captured.reason,
          ...(captured.outcome === 'sent' ? { messageId: captured.messageId } : {}),
        })
      );
      if (
        receipt?.alertId !== captured.alertId ||
        receipt.userId !== captured.userId ||
        receipt.revision !== captured.expectedRevision + 1 ||
        receipt.status !== (captured.outcome === 'sent' ? 'sent' : 'pending')
      )
        throw new Error('Invalid alert review receipt');
      if (!request.isCurrent()) return;
      setNotice(
        captured.outcome === 'sent'
          ? 'Mailbox review saved. This review did not send another alert.'
          : 'Mailbox review saved. The alert is queued; the existing sender can attempt it when enabled and the recipient is still eligible.'
      );
      busyRef.current = false;
      setBusy(false);
      await load(page);
    } catch {
      if (request.isCurrent()) {
        setQueue(null);
        setUncertain(true);
        setError(
          'Low-bank mailbox review was not confirmed. Refresh the queue and check the current attempt before trying again.'
        );
      }
    } finally {
      if (request.isCurrent()) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  };
  return (
    <section
      className="me-distributor-admin-card me-distributor-admin-queue space-y-4"
      aria-label="Low-bank email recovery"
    >
      <h2 className="text-lg font-semibold text-slate-900">Low-bank email recovery</h2>
      {queue && (
        <p role="status">
          {queue.emailEnabled
            ? 'Alert email sending is enabled. Queued alerts use the existing sender.'
            : 'Alert email sending is disabled. Queued alerts will wait; mailbox reviews remain available.'}
        </p>
      )}
      {error && (
        <p role="alert" className="text-red-700">
          {error}
        </p>
      )}
      {notice && (
        <p role="status" className="text-green-700">
          {notice}
        </p>
      )}
      <button
        type="button"
        disabled={loading || busy}
        className="me-distributor-admin-secondary"
        onClick={() => load(page)}
      >
        Refresh alert email queue
      </button>
      {loading && <p role="status">Loading alert emails...</p>}
      {queue?.data.length === 0 && <p>No alert emails need recovery.</p>}
      {queue?.data.map((row) => (
        <div key={rowKey(row)} className="me-distributor-admin-queue-row space-y-2">
          <p>
            <strong>{row.distributorName}</strong> {'\u00b7'}{' '}
            {row.currency === 'USD'
              ? `${usd(row.remainingCents)} credit at alert (threshold ${usd(row.thresholdCents)})`
              : `${row.familyKey} - ${Number(row.remainingUnits).toLocaleString(locale)} historical units at alert`}
          </p>
          <p>
            {row.recipientName} · {roleLabels[row.recipientRole]} · {statusLabels[row.status]}
          </p>
          {row.status === 'pending' && (
            <p className="text-sm text-slate-600">
              Queued for the existing sender. No manual resend is needed.
            </p>
          )}
          {['sending', 'needs_review'].includes(row.status) && !row.canReview && (
            <p className="text-sm text-slate-600">
              An active or incomplete attempt cannot be reviewed yet. Wait at least 15 minutes, then
              refresh. If its claim time is missing, ask MedEffects support.
            </p>
          )}
          {row.canReview && (
            <button
              type="button"
              disabled={busy || loading}
              className="me-distributor-admin-text-button"
              onClick={() =>
                setReview({
                  ...emptyReview,
                  id: rowKey(row),
                  alertId: row.alertId,
                  userId: row.userId,
                  expectedRevision: row.revision,
                })
              }
            >
              Review alert mailbox evidence
            </button>
          )}
          {review.id === rowKey(row) && (
            <>
              {review.outcome === 'not_sent' && (
                <p className="text-sm text-slate-600">
                  Saving Confirmed not sent requeues this alert. If sending is enabled, it can be
                  sent automatically after your review.
                </p>
              )}
              <MailboxReviewForm
                review={review}
                setReview={setReview}
                disabled={busy || loading}
                onSubmit={reconcile}
                onCancel={() => setReview(emptyReview)}
              />
            </>
          )}
        </div>
      ))}
      {queue?.totalPages > 1 && (
        <div className="me-distributor-admin-pagination flex items-center gap-3">
          <button
            type="button"
            disabled={loading || busy || page === 1}
            className="me-distributor-admin-secondary"
            onClick={() => load(page - 1)}
          >
            Previous alert page
          </button>
          <span>
            Page {page} of {queue.totalPages}
          </span>
          <button
            type="button"
            disabled={loading || busy || page >= queue.totalPages}
            className="me-distributor-admin-secondary"
            onClick={() => load(page + 1)}
          >
            Next alert page
          </button>
        </div>
      )}
    </section>
  );
}
